/**
 * Pure sector-aware cash-flow benchmark evaluation.
 *
 * Scoring lives here, not in React. Inputs are copied; comparison charts and
 * raw financial objects are never mutated.
 */

import { getFinancialMetric } from '../config/financialMetricRegistry';
import { resolveMetricSeries } from './financialMetricResolver';
import {
    ARCHETYPE_THRESHOLDS,
    AUDIT_ONLY_METRIC_IDS,
    BENCHMARK_SOURCE_HEURISTIC,
    CAPEX_DA_INVESTIGATION_NOTE,
    COMPARISON_BENCHMARK_MAP,
    DEBT_COVERAGE_METRIC_IDS,
    DIAGNOSTIC_COPY,
    DIAGNOSTIC_ONLY_METRIC_IDS,
    DIVIDEND_COVERAGE_METRIC_IDS,
    DIVIDEND_PAYOUT_METRIC_IDS,
    EARNINGS_CASH_CONVERSION_METRIC_IDS,
    FALLBACK_SECTOR_NOTE,
    FCF_CONVERSION_METRIC_IDS,
    FCF_STABILITY_CAP_ARCHETYPES,
    METHODOLOGY_NOTE,
    NO_DEBT_NOTE,
    NO_DIVIDEND_NOTE,
    PERCENTAGE_METRIC_IDS,
    SELF_SUFFICIENCY_CAPEX_HEAVY_ARCHETYPES,
    SELF_SUFFICIENCY_COPY,
    SELF_SUFFICIENCY_RULE,
    SELF_SUFFICIENCY_SUSTAINED_FLAG_ARCHETYPES,
    STATUS_LABELS,
    WC_LARGE_MOVEMENT_THRESHOLD,
    WC_MOVEMENT_WARNING,
    WC_SIGN_NOTE,
} from '../config/cashFlowBenchmarkConfig';
import {
    CASH_FLOW_RATIOS_PANEL_MAP,
} from '../config/cashFlowRatiosPanelBenchmarkMap';
import {
    applyThreshold,
    calculatePositiveYearsRatio,
    calculateRollingMedian,
    collectAnnualPoints,
    copyAligned,
    formatThresholdRule as formatSharedThresholdRule,
    isVolatile,
    median,
} from './benchmarkMath';
import {
    buildSectorBenchmarkProfile,
    isNotApplicableSector,
    normalizeSector,
} from './sectorResolver';

export { applyThreshold, calculatePositiveYearsRatio, calculateRollingMedian };
export { isNotApplicableSector, normalizeSector };

export function formatThresholdRule(metricId, spec, requiredWindow) {
    return formatSharedThresholdRule(metricId, spec, requiredWindow, {
        label: getFinancialMetric(metricId)?.label || metricId,
    });
}

/**
 * @param {unknown} rawSector
 */
export function getCashFlowBenchmarkProfile(rawSector) {
    return buildSectorBenchmarkProfile(rawSector, ARCHETYPE_THRESHOLDS);
}


function evaluateFcfStability(fcfValues, cfoValues, years) {
    const fcfPoints = collectAnnualPoints(fcfValues, years);
    const cfoPoints = collectAnnualPoints(cfoValues, years);
    const fcfByYear = new Map(fcfPoints.map((point) => [point.year, point.value]));
    const validCfo = cfoPoints.filter((point) => point.value > 0);
    const window = validCfo.slice(-5);
    let positiveFcfYears = 0;
    window.forEach((point) => {
        const fcf = fcfByYear.get(point.year);
        if (fcf != null && fcf > 0) positiveFcfYears += 1;
    });
    const validCfoYears = window.length;
    return {
        positiveFcfYears,
        validCfoYears,
        ratio: validCfoYears ? positiveFcfYears / validCfoYears : null,
        unstable: validCfoYears >= 3 && positiveFcfYears < 3,
    };
}

function buildInterpretation(metricId, status, profile, extras = {}) {
    const sectorLabel = profile.sector.label;
    const metricLabel = getFinancialMetric(metricId)?.label || metricId;
    if (status === 'notApplicable') {
        return extras.notApplicableReason || profile.notApplicableReason || `${metricLabel} is not applicable for this company type.`;
    }
    if (status === 'insufficientData') {
        return `Fewer than ${profile.requiredWindow} valid annual observations are available for a ${sectorLabel} sector score. Latest year is shown as context only.`;
    }
    if (status === 'diagnosticOnly') {
        return extras.interpretation || 'This chart is diagnostic cash-flow context and is not given a sector traffic-light score.';
    }
    if (metricId === 'capexToDepreciation') {
        if (status === 'strong') return `Capex / Depreciation sits inside the typical ${sectorLabel} band.`;
        if (status === 'watch') return `Capex / Depreciation is outside the core ${sectorLabel} band and warrants explanation against growth plans and capacity utilization.`;
        return CAPEX_DA_INVESTIGATION_NOTE;
    }
    if (metricId === 'workingCapitalToCfo') {
        const bandWord = status === 'strong' ? 'inside' : (status === 'watch' ? 'near' : 'outside');
        return `${WC_SIGN_NOTE} The multi-year ratio is ${bandWord} the ${sectorLabel} normal band.`;
    }
    if (EARNINGS_CASH_CONVERSION_METRIC_IDS.has(metricId)) {
        if (status === 'strong') return `Accounting earnings are converting into cash at a rate that meets the initial ${sectorLabel} screening threshold.`;
        if (status === 'watch') return `Earnings-to-cash conversion is below the strong ${sectorLabel} threshold and warrants explanation.`;
        return `Earnings-to-cash conversion is materially below the ${sectorLabel} screening threshold and requires investigation.`;
    }
    if (DIVIDEND_COVERAGE_METRIC_IDS.has(metricId)) {
        if (status === 'strong') return `Free cash flow covers dividends at a rate that meets the initial ${sectorLabel} screening threshold.`;
        if (status === 'watch') return `Dividend coverage is below the strong ${sectorLabel} threshold and warrants explanation.`;
        return `Dividends are not comfortably covered by free cash flow on the ${sectorLabel} screening rule and require investigation.`;
    }
    if (status === 'strong') return `Multi-year ${metricLabel} meets the initial ${sectorLabel} screening threshold.`;
    if (status === 'watch') return `Multi-year ${metricLabel} is below the strong ${sectorLabel} threshold and warrants explanation.`;
    return `Multi-year ${metricLabel} is materially below the ${sectorLabel} screening threshold and requires investigation.`;
}

function emptyEvaluation(overrides) {
    return {
        metricId: overrides.metricId,
        label: getFinancialMetric(overrides.metricId)?.label || overrides.metricId,
        status: overrides.status,
        latestStatus: overrides.latestStatus || null,
        primaryValue: overrides.primaryValue ?? null,
        latestValue: overrides.latestValue ?? null,
        median3: overrides.median3 ?? null,
        median5: overrides.median5 ?? null,
        min: overrides.min ?? null,
        max: overrides.max ?? null,
        validAnnualCount: overrides.validAnnualCount || 0,
        positiveYearCount: overrides.positiveYearCount || 0,
        positiveYearRatio: overrides.positiveYearRatio ?? null,
        windowUsed: overrides.windowUsed || null,
        ruleText: overrides.ruleText || '',
        interpretation: overrides.interpretation || '',
        warnings: overrides.warnings || [],
        volatilityWarning: Boolean(overrides.volatilityWarning),
        conservativeCoverage: overrides.conservativeCoverage ?? null,
        fcfStability: overrides.fcfStability || null,
        source: BENCHMARK_SOURCE_HEURISTIC,
        unit: PERCENTAGE_METRIC_IDS.has(overrides.metricId) ? 'percentage' : (getFinancialMetric(overrides.metricId)?.unit || 'multiple'),
        notApplicableReason: overrides.notApplicableReason || null,
    };
}

const NEGATIVE_FCF_REASON = 'Negative free cash flow: the business consumed more cash than it generated after reinvestment this year.';
const NEGATIVE_OWNER_EARNINGS_REASON = 'Negative owner earnings: operating cash did not cover gross reinvestment this year.';
const NEGATIVE_CASH_ROIC_REASON = 'Negative cash ROIC: free cash flow did not earn a positive return on invested capital this year.';

/**
 * Universal sign/extremity distress flags for absolute-value Cash Flow Ratios
 * rows that are intentionally excluded from sector-aware threshold scoring.
 *
 * This is not a benchmark. It is a sector-blind, magnitude-blind sign check
 * (strictly negative → flagged). It does not consult sector, archetype, or
 * ARCHETYPE_THRESHOLDS.
 *
 * | rowKey | Distress rule | Notes |
 * |---|---|---|
 * | fcfAbsolute / reportedFcf | value < 0 | Negative FCF is a basic caution signal. |
 * | ownerEarnings | value < 0 | Negative owner earnings is a basic caution signal. |
 * | workingCapitalDelta / workingCapitalChanges | none | A negative ΔWC is not inherently bad (growth-driven absorption). |
 * | cashRatio | none | Balance-sheet liquidity; a calibrated liquidity-risk rule is out of scope. |
 * | cashRoic | value < 0 | Negative cash return on invested capital. |
 * | grossCapexFcf | none | Not a table row; Owner Earnings uses a different formula. |
 */
const DISTRESS_FLAG_RULES = {
    fcfAbsolute: { reason: NEGATIVE_FCF_REASON },
    reportedFcf: { reason: NEGATIVE_FCF_REASON },
    ownerEarnings: { reason: NEGATIVE_OWNER_EARNINGS_REASON },
    cashRoic: { reason: NEGATIVE_CASH_ROIC_REASON },
};

export const DISTRESS_FLAG_CAPTION = 'This value is not scored against a sector benchmark because it scales with company size. Negative years are flagged as a basic caution signal.';

export function hasDistressFlagRule(rowKey) {
    return Boolean(rowKey && DISTRESS_FLAG_RULES[rowKey]);
}

/**
 * Universal sign/extremity distress flag for absolute-value rows that are
 * intentionally excluded from sector-aware threshold scoring. This is not
 * a benchmark; it is a basic sanity signal that a value is directionally
 * bad regardless of sector or company size.
 *
 * @param {object} params
 * @param {string} params.rowKey  // e.g. 'fcfAbsolute', 'reportedFcf', 'ownerEarnings'
 * @param {Array<number|null>} params.values
 * @param {string[]} params.years
 * @returns {Array<{ year: string, value: number, isDistress: boolean, reason: string }>}
 */
export function evaluateDistressFlags({ rowKey, values, years } = {}) {
    const copied = copyAligned(values, years);
    const points = collectAnnualPoints(copied.values, copied.years);
    const rule = DISTRESS_FLAG_RULES[rowKey];
    return points.map((point) => {
        const isDistress = Boolean(rule) && point.value < 0;
        return {
            year: point.year,
            value: point.value,
            isDistress,
            reason: isDistress ? rule.reason : '',
        };
    });
}

/**
 * Apply the same sector threshold to each valid annual observation.
 * Does not change the row-level median status from evaluateCashFlowMetric.
 *
 * @param {object} params
 * @param {string} params.metricId
 * @param {unknown} params.sector
 * @param {Array<number|null>} [params.values]
 * @param {string[]} [params.years]
 * @returns {Array<{ year: string, value: number, status: string|null }>}
 */
export function evaluatePerYearCashFlowStatus({ metricId, sector, values, years } = {}) {
    const profile = getCashFlowBenchmarkProfile(sector);
    const spec = metricId ? profile.metrics?.[metricId] : null;
    const copied = copyAligned(values, years);
    return collectAnnualPoints(copied.values, copied.years).map((point) => ({
        year: point.year,
        value: point.value,
        status: applyThreshold(point.value, spec),
    }));
}

/**
 * Evaluate one normalized cash-flow metric against the sector heuristic.
 *
 * @param {object} params
 * @param {string} params.metricId
 * @param {unknown} params.sector
 * @param {Array<number|null>} [params.values]
 * @param {string[]} [params.years]
 * @param {{ fcfValues?: Array<number|null>, cfoValues?: Array<number|null>, dividendValues?: Array<number|null>, grossCapexFcfValues?: Array<number|null> }} [params.context]
 */
export function evaluateCashFlowMetric({
    metricId,
    sector,
    values,
    years,
    context = {},
} = {}) {
    const profile = getCashFlowBenchmarkProfile(sector);
    const copied = copyAligned(values, years);
    const warnings = [];
    if (profile.sector.unmapped) warnings.push(FALLBACK_SECTOR_NOTE);

    if (!metricId) {
        return emptyEvaluation({
            metricId: 'unknown',
            status: 'insufficientData',
            interpretation: 'No metric was supplied for evaluation.',
            warnings,
        });
    }

    if (AUDIT_ONLY_METRIC_IDS.has(metricId)) {
        return emptyEvaluation({
            metricId,
            status: 'notApplicable',
            notApplicableReason: 'CFO / Operating Profit is an audit-only source field and is not scored as a sector benchmark.',
            interpretation: 'CFO / Operating Profit is an audit-only source field and is not scored as a sector benchmark.',
            warnings,
        });
    }

    if (DIAGNOSTIC_ONLY_METRIC_IDS.has(metricId)) {
        return emptyEvaluation({
            metricId,
            status: 'diagnosticOnly',
            interpretation: 'Absolute cash-flow totals are diagnostic context and are not given a sector traffic-light score.',
            ruleText: 'No numeric sector threshold is applied to absolute cash-flow totals.',
            windowUsed: profile.primaryWindowLabel,
            warnings,
        });
    }

    if (isNotApplicableSector(profile.sector)) {
        return emptyEvaluation({
            metricId,
            status: 'notApplicable',
            notApplicableReason: profile.notApplicableReason,
            interpretation: profile.notApplicableReason,
            windowUsed: profile.primaryWindowLabel,
            warnings,
        });
    }

    const spec = profile.metrics?.[metricId];
    if (!spec) {
        return emptyEvaluation({
            metricId,
            status: 'notApplicable',
            notApplicableReason: `${getFinancialMetric(metricId)?.label || metricId} has no sector-aware cash-flow benchmark.`,
            interpretation: `${getFinancialMetric(metricId)?.label || metricId} has no sector-aware cash-flow benchmark.`,
            warnings,
        });
    }

    const ruleText = formatThresholdRule(metricId, spec, profile.requiredWindow);
    const annual = collectAnnualPoints(copied.values, copied.years);
    const annualValues = annual.map((point) => point.value);
    const latestPoint = annual.length ? annual[annual.length - 1] : null;
    const latestValue = latestPoint ? latestPoint.value : null;
    const median3 = annual.length >= 3 ? median(annual.slice(-3).map((point) => point.value)) : null;
    const median5 = annual.length >= 5 ? median(annual.slice(-5).map((point) => point.value)) : null;
    const primaryValue = profile.requiredWindow === 5 ? median5 : median3;
    const min = annualValues.length ? Math.min(...annualValues) : null;
    const max = annualValues.length ? Math.max(...annualValues) : null;
    const positiveYearCount = annualValues.filter((value) => value > 0).length;
    const positiveYearRatio = calculatePositiveYearsRatio(annualValues);
    const latestStatus = applyThreshold(latestValue, spec);

    if (
        (DIVIDEND_COVERAGE_METRIC_IDS.has(metricId) || DIVIDEND_PAYOUT_METRIC_IDS.has(metricId))
        && Array.isArray(context.dividendValues)
    ) {
        const dividendPoints = collectAnnualPoints(context.dividendValues, copied.years.length ? copied.years : years);
        const hasOutflow = dividendPoints.some((point) => point.value != null && Number.isFinite(point.value) && point.value !== 0);
        if (!hasOutflow) {
            return emptyEvaluation({
                metricId,
                status: 'notApplicable',
                notApplicableReason: NO_DIVIDEND_NOTE,
                interpretation: NO_DIVIDEND_NOTE,
                ruleText,
                windowUsed: profile.primaryWindowLabel,
                latestValue,
                validAnnualCount: annual.length,
                warnings,
            });
        }
    }

    if (DEBT_COVERAGE_METRIC_IDS.has(metricId) && Array.isArray(context.borrowingsValues)) {
        const debtPoints = collectAnnualPoints(context.borrowingsValues, copied.years.length ? copied.years : years);
        const hasDebt = debtPoints.some((point) => point.value != null && Number.isFinite(point.value) && point.value !== 0);
        if (!hasDebt) {
            return emptyEvaluation({
                metricId,
                status: 'notApplicable',
                notApplicableReason: NO_DEBT_NOTE,
                interpretation: NO_DEBT_NOTE,
                ruleText,
                windowUsed: profile.primaryWindowLabel,
                latestValue,
                validAnnualCount: annual.length,
                warnings,
            });
        }
    }

    if (annual.length < profile.requiredWindow || primaryValue == null) {
        return emptyEvaluation({
            metricId,
            status: 'insufficientData',
            latestStatus,
            latestValue,
            primaryValue: null,
            median3,
            median5,
            min,
            max,
            validAnnualCount: annual.length,
            positiveYearCount,
            positiveYearRatio,
            windowUsed: profile.primaryWindowLabel,
            ruleText,
            interpretation: buildInterpretation(metricId, 'insufficientData', profile),
            warnings: annual.length ? [...warnings, `Only ${annual.length} valid annual observation(s); ${profile.requiredWindow} are required for a sector score.`] : warnings,
        });
    }

    let status = applyThreshold(primaryValue, spec) || 'watch';
    const volatilityWarning = isVolatile(latestValue, primaryValue, spec);
    if (volatilityWarning) {
        warnings.push('Latest year diverges from the multi-year median; treat the latest reading as context, not as the sector score.');
    }

    let fcfStability = null;
    if (FCF_CONVERSION_METRIC_IDS.has(metricId) && (context.fcfValues || context.cfoValues)) {
        fcfStability = evaluateFcfStability(context.fcfValues, context.cfoValues, copied.years);
        if (fcfStability.unstable) {
            warnings.push(`Free cash flow was positive in ${fcfStability.positiveFcfYears} of the ${fcfStability.validCfoYears} most recent CFO-positive years.`);
            if (FCF_STABILITY_CAP_ARCHETYPES.has(profile.archetypeId) && status === 'strong') {
                status = 'watch';
                warnings.push('FCF conversion is capped at Watch because fewer than 3 of the last 5 CFO-positive years produced positive FCF.');
            }
        }
    }

    if (metricId === 'workingCapitalToCfo') {
        if (latestValue != null && Math.abs(latestValue) > WC_LARGE_MOVEMENT_THRESHOLD) {
            warnings.push(WC_MOVEMENT_WARNING);
        }
        if (status === 'strong' && latestValue != null && latestValue > WC_LARGE_MOVEMENT_THRESHOLD) {
            status = 'watch';
            warnings.push('A single large working-capital release is not treated as a strong multi-year result.');
        }
    }

    if (DIVIDEND_COVERAGE_METRIC_IDS.has(metricId)) {
        const fcfSeries = metricId === 'grossCapexFcfDividendCoverage' ? context.grossCapexFcfValues : context.fcfValues;
        const fcfPrimary = fcfSeries ? calculateRollingMedian(fcfSeries, copied.years, profile.requiredWindow) : null;
        const fcfLatest = fcfSeries ? (collectAnnualPoints(fcfSeries, copied.years).pop()?.value ?? null) : null;
        if ((fcfPrimary != null && fcfPrimary < 0) || (fcfLatest != null && fcfLatest < 0) || primaryValue < 0) {
            status = 'weak';
            warnings.push('Free cash flow is negative while dividends were paid; coverage is treated as requiring investigation.');
        }
    }

    let conservativeCoverage = null;
    if (
        (metricId === 'dividendCoverage' || metricId === 'grossCapexFcfDividendCoverage')
        && Array.isArray(context.grossCapexFcfValues)
        && Array.isArray(context.dividendValues)
    ) {
        const gcfMedian = calculateRollingMedian(context.grossCapexFcfValues, copied.years, profile.requiredWindow);
        const divPoints = collectAnnualPoints(context.dividendValues, copied.years).slice(-profile.requiredWindow);
        const absDiv = median(divPoints.map((point) => Math.abs(point.value)).filter((value) => value > 0));
        if (gcfMedian != null && absDiv) {
            conservativeCoverage = gcfMedian / absDiv;
        }
    }

    return {
        metricId,
        label: getFinancialMetric(metricId)?.label || metricId,
        status,
        latestStatus,
        primaryValue,
        latestValue,
        median3,
        median5,
        min,
        max,
        validAnnualCount: annual.length,
        positiveYearCount,
        positiveYearRatio,
        windowUsed: profile.primaryWindowLabel,
        ruleText,
        interpretation: buildInterpretation(metricId, status, profile),
        warnings,
        volatilityWarning,
        conservativeCoverage,
        fcfStability,
        source: BENCHMARK_SOURCE_HEURISTIC,
        unit: spec.unit || (PERCENTAGE_METRIC_IDS.has(metricId) ? 'percentage' : 'multiple'),
        notApplicableReason: null,
    };
}

export function getCashFlowBenchmarkForMetric({
    metricId,
    sector,
    values,
    years,
    context,
} = {}) {
    const profile = getCashFlowBenchmarkProfile(sector);
    const spec = (profile.metrics && profile.metrics[metricId]) || null;
    return {
        profile,
        spec,
        ruleText: spec ? formatThresholdRule(metricId, spec, profile.requiredWindow) : '',
        evaluation: evaluateCashFlowMetric({ metricId, sector, values, years, context }),
    };
}

function classifySelfSufficiencyYear(cfo, cfi, cff) {
    if (cfo == null || cfi == null || cff == null) return null;
    if (!Number.isFinite(cfo) || !Number.isFinite(cfi) || !Number.isFinite(cff)) return null;
    const netInvFin = cfi + cff;
    if (netInvFin >= 0) return 'externallyFunded';
    return Math.abs(netInvFin) <= cfo ? 'selfFunded' : 'externallyFunded';
}

function selfSufficiencyInterpretation(status, profile, extras = {}) {
    if (status === 'notApplicable') {
        if (profile.sector.sectorId === 'financialInstitutions') {
            return SELF_SUFFICIENCY_COPY.financialInstitutions;
        }
        return extras.notApplicableReason || profile.notApplicableReason
            || 'This cash self-sufficiency check is not applicable for this company type.';
    }
    if (status === 'insufficientData') {
        return `Fewer than ${profile.requiredWindow} valid annual CFO/CFI/CFF observations are available for a self-sufficiency check.`;
    }
    if (status === 'selfFunded') {
        return SELF_SUFFICIENCY_COPY.selfFunded;
    }
    if (SELF_SUFFICIENCY_CAPEX_HEAVY_ARCHETYPES.has(profile.archetypeId)) {
        return `${SELF_SUFFICIENCY_COPY.externallyFundedDefault} ${SELF_SUFFICIENCY_COPY.capexHeavy}`;
    }
    if (SELF_SUFFICIENCY_SUSTAINED_FLAG_ARCHETYPES.has(profile.archetypeId)) {
        return `${SELF_SUFFICIENCY_COPY.externallyFundedDefault} ${SELF_SUFFICIENCY_COPY.sustainedFlag}`;
    }
    return SELF_SUFFICIENCY_COPY.externallyFundedDefault;
}

/**
 * Qualitative cash self-sufficiency check: does CFO cover the net CFI+CFF outflow?
 * Not a Strong/Watch/Weak score.
 *
 * @param {object} params
 * @param {unknown} params.sector
 * @param {Array<number|null>} [params.cfoValues]
 * @param {Array<number|null>} [params.cfiValues]
 * @param {Array<number|null>} [params.cffValues]
 * @param {string[]} [params.years]
 */
export function evaluateCashSelfSufficiency({
    sector,
    cfoValues,
    cfiValues,
    cffValues,
    years,
} = {}) {
    const profile = getCashFlowBenchmarkProfile(sector);
    const warnings = [];
    if (profile.sector.unmapped) warnings.push(FALLBACK_SECTOR_NOTE);

    const windowLabel = profile.primaryWindowLabel;
    const ruleText = `${SELF_SUFFICIENCY_RULE} Evaluated on the ${windowLabel.replace(' median', '')} window.`;

    if (isNotApplicableSector(profile.sector)) {
        const reason = profile.sector.sectorId === 'financialInstitutions'
            ? SELF_SUFFICIENCY_COPY.financialInstitutions
            : profile.notApplicableReason;
        return {
            ...emptyEvaluation({
                metricId: 'cashSelfSufficiency',
                status: 'notApplicable',
                notApplicableReason: reason,
                interpretation: selfSufficiencyInterpretation('notApplicable', profile, { notApplicableReason: reason }),
                ruleText,
                windowUsed: windowLabel,
                warnings,
            }),
            yearStatuses: [],
        };
    }

    const cfoCopied = copyAligned(cfoValues, years);
    const annualYears = collectAnnualPoints(cfoCopied.values, cfoCopied.years).map((point) => point.year);
    const cfoByYear = new Map(collectAnnualPoints(cfoCopied.values, cfoCopied.years).map((point) => [point.year, point.value]));
    const cfiByYear = new Map(collectAnnualPoints(cfiValues, cfoCopied.years).map((point) => [point.year, point.value]));
    const cffByYear = new Map(collectAnnualPoints(cffValues, cfoCopied.years).map((point) => [point.year, point.value]));

    const classified = [];
    annualYears.forEach((year) => {
        const status = classifySelfSufficiencyYear(cfoByYear.get(year), cfiByYear.get(year), cffByYear.get(year));
        if (status) classified.push({ year, status, cfo: cfoByYear.get(year) });
    });

    const latest = classified.length ? classified[classified.length - 1] : null;
    const yearStatuses = classified.map((point) => ({ year: point.year, status: point.status }));
    if (classified.length < profile.requiredWindow) {
        return {
            ...emptyEvaluation({
                metricId: 'cashSelfSufficiency',
                status: 'insufficientData',
                latestStatus: latest ? latest.status : null,
                latestValue: latest ? latest.cfo : null,
                validAnnualCount: classified.length,
                windowUsed: windowLabel,
                ruleText,
                interpretation: selfSufficiencyInterpretation('insufficientData', profile),
                warnings: classified.length
                    ? [...warnings, `Only ${classified.length} valid annual observation(s); ${profile.requiredWindow} are required.`]
                    : warnings,
            }),
            yearStatuses,
        };
    }

    const window = classified.slice(-profile.requiredWindow);
    const selfCount = window.filter((point) => point.status === 'selfFunded').length;
    const status = selfCount > window.length / 2 ? 'selfFunded' : 'externallyFunded';

    return {
        ...emptyEvaluation({
            metricId: 'cashSelfSufficiency',
            status,
            latestStatus: latest.status,
            latestValue: latest.cfo,
            validAnnualCount: classified.length,
            positiveYearCount: selfCount,
            positiveYearRatio: window.length ? selfCount / window.length : null,
            windowUsed: windowLabel,
            ruleText,
            interpretation: selfSufficiencyInterpretation(status, profile),
            warnings,
        }),
        yearStatuses,
    };
}

/**
 * Evaluate every mapped Cash Flow Ratios table row against the shared resolver.
 *
 * @param {object} params
 * @param {unknown} params.sector
 * @param {object} [params.data]
 * @param {Array} [params.allRatios]
 * @param {string} [params.preferredBasis]
 */
export function evaluateCashFlowRatiosPanel({
    sector,
    data,
    allRatios = [],
    preferredBasis = 'consolidated',
} = {}) {
    const profile = getCashFlowBenchmarkProfile(sector);
    let years = [];
    try {
        const cfoSeries = resolveMetricSeries({
            data,
            metricId: 'cfo',
            allRatios,
            preferredBasis,
        });
        years = (cfoSeries || []).map((point) => String(point.year));
    } catch (_err) {
        years = [];
    }

    const resolveOpts = {
        comparisonChart: null,
        years,
        data,
        allRatios,
        preferredBasis,
    };
    const context = {
        fcfValues: resolveAlignedValues({ metricId: 'reportedFcf', ...resolveOpts }),
        cfoValues: resolveAlignedValues({ metricId: 'cfo', ...resolveOpts }),
        dividendValues: resolveAlignedValues({ metricId: 'dividendsPaid', ...resolveOpts }),
        grossCapexFcfValues: resolveAlignedValues({ metricId: 'grossCapexFcf', ...resolveOpts }),
        borrowingsValues: resolveAlignedValues({ metricId: 'borrowings', ...resolveOpts }),
        cfiValues: resolveAlignedValues({ metricId: 'cfi', ...resolveOpts }),
        cffValues: resolveAlignedValues({ metricId: 'cff', ...resolveOpts }),
    };

    const byRatioName = {};
    Object.entries(CASH_FLOW_RATIOS_PANEL_MAP).forEach(([ratioName, mapping]) => {
        if (mapping.kind === 'notScored') {
            byRatioName[ratioName] = {
                kind: 'notScored',
                reason: mapping.reason,
                evaluation: null,
            };
            return;
        }
        if (mapping.kind === 'selfSufficiency') {
            const evaluation = evaluateCashSelfSufficiency({
                sector,
                cfoValues: context.cfoValues,
                cfiValues: context.cfiValues,
                cffValues: context.cffValues,
                years,
            });
            byRatioName[ratioName] = {
                kind: 'selfSufficiency',
                evaluation,
                yearStatuses: evaluation.yearStatuses || [],
            };
            return;
        }
        const values = resolveAlignedValues({ metricId: mapping.metricId, ...resolveOpts });
        byRatioName[ratioName] = {
            kind: 'scored',
            metricId: mapping.metricId,
            evaluation: evaluateCashFlowMetric({
                metricId: mapping.metricId,
                sector,
                values,
                years,
                context,
            }),
            yearStatuses: evaluatePerYearCashFlowStatus({
                metricId: mapping.metricId,
                sector,
                values,
                years,
            }),
        };
    });

    return {
        profile,
        byRatioName,
        methodologyNote: METHODOLOGY_NOTE,
        source: BENCHMARK_SOURCE_HEURISTIC,
    };
}

function comparisonIdOf(comparison) {
    if (!comparison) return null;
    if (typeof comparison === 'string') return comparison;
    return comparison.id || null;
}

function chartYears(comparisonChart) {
    return Array.isArray(comparisonChart?.years) ? comparisonChart.years.slice() : [];
}

function valuesFromChart(comparisonChart, metricId) {
    const series = Array.isArray(comparisonChart?.series)
        ? comparisonChart.series.find((item) => item && item.metricId === metricId)
        : null;
    if (!series || !Array.isArray(series.values)) return null;
    return series.values.slice();
}

function alignSeriesToYears(series, years) {
    const map = new Map();
    (series || []).forEach((point) => {
        if (point && point.year != null) map.set(String(point.year), point.value);
    });
    return years.map((year) => (map.has(String(year)) ? map.get(String(year)) : null));
}

function resolveAlignedValues({
    metricId,
    comparisonChart,
    years,
    data,
    allRatios,
    preferredBasis,
}) {
    const fromChart = valuesFromChart(comparisonChart, metricId);
    if (fromChart && fromChart.length) return fromChart;
    if (!data || !metricId) return years.map(() => null);
    try {
        const series = resolveMetricSeries({
            data,
            metricId,
            allRatios,
            preferredBasis,
        });
        return alignSeriesToYears(series, years);
    } catch (_err) {
        return years.map(() => null);
    }
}

function hasNumeric(values) {
    return (values || []).some((value) => value != null && Number.isFinite(value));
}

function diagnosticInterpretation(comparisonId, profile) {
    const copy = DIAGNOSTIC_COPY[comparisonId] || {};
    if (profile.sector.sectorId === 'financialInstitutions' && copy.financial) return copy.financial;
    return copy[profile.archetypeId] || copy.default || 'This chart is diagnostic cash-flow context and is not given a sector traffic-light score.';
}

function buildDiagnosticResult(comparisonId, profile) {
    const card = {
        metricId: 'diagnostic',
        label: 'Sector diagnostic',
        status: 'diagnosticOnly',
        latestStatus: null,
        primaryValue: null,
        latestValue: null,
        median3: null,
        median5: null,
        min: null,
        max: null,
        validAnnualCount: 0,
        positiveYearCount: 0,
        positiveYearRatio: null,
        windowUsed: profile.primaryWindowLabel,
        ruleText: 'No numeric sector threshold is applied to absolute cash-flow totals.',
        interpretation: diagnosticInterpretation(comparisonId, profile),
        warnings: profile.sector.unmapped ? [FALLBACK_SECTOR_NOTE] : [],
        volatilityWarning: false,
        conservativeCoverage: null,
        fcfStability: null,
        source: BENCHMARK_SOURCE_HEURISTIC,
        unit: null,
        notApplicableReason: null,
    };
    return {
        profile,
        cards: [card],
        defaultCardIds: ['diagnostic'],
        methodologyNote: METHODOLOGY_NOTE,
        source: BENCHMARK_SOURCE_HEURISTIC,
    };
}

/**
 * Evaluate the benchmark cards for a named Cash Flow comparison.
 * Supporting ratios are resolved via resolveMetricSeries and are not added to the chart.
 *
 * @param {object} params
 * @param {object|string} params.comparison
 * @param {unknown} params.sector
 * @param {object} [params.comparisonChart]
 * @param {object} [params.data]
 * @param {Array} [params.allRatios]
 * @param {string} [params.preferredBasis]
 * @returns {object|null}
 */
export function evaluateComparisonBenchmarks({
    comparison,
    sector,
    comparisonChart,
    data,
    allRatios = [],
    preferredBasis = 'consolidated',
} = {}) {
    const comparisonId = comparisonIdOf(comparison);
    if (comparison?.legacy) return null;
    const spec = COMPARISON_BENCHMARK_MAP[comparisonId];
    if (!spec) return null;

    const profile = getCashFlowBenchmarkProfile(sector);
    if (spec.mode === 'diagnostic') {
        return buildDiagnosticResult(comparisonId, profile);
    }

    const years = chartYears(comparisonChart);
    const resolveOpts = {
        comparisonChart,
        years,
        data,
        allRatios,
        preferredBasis,
    };
    const context = {
        fcfValues: resolveAlignedValues({ metricId: 'reportedFcf', ...resolveOpts }),
        cfoValues: resolveAlignedValues({ metricId: 'cfo', ...resolveOpts }),
        dividendValues: resolveAlignedValues({ metricId: 'dividendsPaid', ...resolveOpts }),
        grossCapexFcfValues: resolveAlignedValues({ metricId: 'grossCapexFcf', ...resolveOpts }),
    };

    const optional = new Set(spec.optionalMetricIds || []);
    const cards = [];
    (spec.metricIds || []).forEach((metricId) => {
        const values = resolveAlignedValues({ metricId, ...resolveOpts });
        if (optional.has(metricId) && !hasNumeric(values)) return;
        const evaluation = evaluateCashFlowMetric({
            metricId,
            sector,
            values,
            years,
            context,
        });
        if (spec.notes) {
            spec.notes.forEach((note) => {
                if (!evaluation.warnings.includes(note)) evaluation.warnings.push(note);
            });
        }
        cards.push(evaluation);
    });

    const defaultCardIds = (spec.defaultMetricIds || []).filter((id) => cards.some((card) => card.metricId === id)).slice(0, 2);

    return {
        profile,
        cards,
        defaultCardIds: defaultCardIds.length ? defaultCardIds : cards.slice(0, 2).map((card) => card.metricId),
        methodologyNote: METHODOLOGY_NOTE,
        source: BENCHMARK_SOURCE_HEURISTIC,
        includeStability: Boolean(spec.includeStability),
    };
}

export { STATUS_LABELS };
